// Contract with the Rewards flyout page (served on the third-party bing.com origin):
// once the flyout has successfully loaded it must notify its OWA parent with
//     parent.postMessage(
//         { type: 'rewards-flyout-ready', anidMatch: true | false, status: 'ok' | 'error' },
//         <owa-origin>)
//
// `anidMatch` is the flyout's server-side verdict on whether the ANID OWA sent outbound (`owaAnid`
// on the flyout URL) matches the Bing session's ANID. The comparison happens on the flyout server
// and only a boolean is returned, so a mismatching account's identifier is never disclosed to OWA.
// OWA shows the iframe only when `anidMatch === true`; any other value fails safe to an error screen.
//
// `status` reports whether the flyout rendered normally ('ok') or the Rewards platform returned an
// error while the page itself still loaded ('error', e.g. a backend failure or unsupported market).
// This lets OWA show a generic "something went wrong" screen instead of the cookie-specific one for
// failures where the flyout's code actually ran. A true 404/500 of the flyout route never runs the
// flyout's script, so it cannot be reported here and remains indistinguishable from cookies blocked.
//
// The flyout only loads when third-party cookies are available. When they are blocked the flyout's
// auth navigation hard-fails (the browser renders an internal error frame such as
// chrome-error://chromewebdata/) and no bing code ever runs, so the ready signal can never arrive.
// We cannot read the cross-origin iframe directly, so the *absence* of the ready signal within a
// generous timeout is treated as "third-party cookies blocked".

export const REWARDS_FLYOUT_ORIGIN = 'https://www.bing.com';

export const REWARDS_FLYOUT_READY_MESSAGE_TYPE = 'rewards-flyout-ready';

// Generous default so slow networks don't produce a false "blocked" result before the flyout has a
// chance to load and post its ready signal.
const READY_TIMEOUT_MS = 8000;

export interface FlyoutReadyResult {
    // True when the flyout never signalled readiness within the timeout (third-party cookies
    // appear to be blocked).
    blocked: boolean;
    // The flyout's server-side verdict on whether the OWA account matches the Bing session account.
    // Undefined when the flyout did not report it (older flyout pages) or when blocked.
    anidMatch?: boolean;
    // True when the flyout loaded but the Rewards platform reported an error (status 'error').
    // Undefined when the flyout did not report a status (older flyout pages) or when blocked.
    flyoutError?: boolean;
}

interface FlyoutReadyMessage {
    type: string;
    anidMatch?: unknown;
    status?: unknown;
}

function isFlyoutReadyMessage(data: unknown): data is FlyoutReadyMessage {
    return (
        typeof data === 'object' &&
        data !== null &&
        (data as FlyoutReadyMessage).type === REWARDS_FLYOUT_READY_MESSAGE_TYPE
    );
}

/**
 * Waits for the Rewards flyout iframe to announce a successful load via `postMessage`, and reports
 * both whether third-party cookies appear blocked and the flyout's ANID-match verdict.
 *
 * The caller is responsible for rendering the flyout iframe; this function only listens for the
 * flyout's ready signal. Because a cross-origin iframe cannot be inspected directly, we rely on the
 * flyout telling us it loaded: if the signal arrives cookies are available (`blocked: false`), and
 * if it never arrives within `timeoutMs` we assume cookies are blocked (`blocked: true`).
 *
 * @param getFlyoutWindow Returns the Rewards iframe's `contentWindow` (or `null`/`undefined` if it
 * has not mounted yet). The ready signal is only trusted when it originates from this exact window,
 * so a different frame on the same origin cannot spoof it. Read lazily on each message so the
 * caller can populate it once the iframe loads.
 * @param timeoutMs How long to wait for the ready signal before assuming cookies are blocked.
 * @returns `{ blocked: true }` when third-party cookies appear to be blocked, otherwise
 * `{ blocked: false, anidMatch, flyoutError }` with the flyout's match verdict and whether the
 * Rewards platform reported an error (each a boolean, or `undefined` when the flyout did not report
 * one).
 */
export default function waitForFlyoutReady(
    getFlyoutWindow: () => Window | null | undefined,
    timeoutMs: number = READY_TIMEOUT_MS
): Promise<FlyoutReadyResult> {
    return new Promise<FlyoutReadyResult>(resolve => {
        if (typeof window === 'undefined') {
            resolve({ blocked: false });
            return;
        }

        let settled = false;
        let timeoutId: number | undefined;

        const cleanup = () => {
            window.removeEventListener('message', handleMessage);
            if (timeoutId !== undefined) {
                window.clearTimeout(timeoutId);
                timeoutId = undefined;
            }
        };

        const settle = (result: FlyoutReadyResult) => {
            if (settled) {
                return;
            }
            settled = true;
            cleanup();
            resolve(result);
        };

        function handleMessage(event: MessageEvent) {
            const flyoutWindow = getFlyoutWindow();
            if (
                event.origin !== REWARDS_FLYOUT_ORIGIN ||
                !isFlyoutReadyMessage(event.data) ||
                !flyoutWindow ||
                event.source !== flyoutWindow
            ) {
                return;
            }
            const anidMatch =
                typeof event.data.anidMatch === 'boolean' ? event.data.anidMatch : undefined;
            const flyoutError =
                typeof event.data.status === 'string' ? event.data.status === 'error' : undefined;
            settle({ blocked: false, anidMatch, flyoutError });
        }

        window.addEventListener('message', handleMessage);
        timeoutId = window.setTimeout(() => settle({ blocked: true }), timeoutMs);
    });
}
